import {
  EnvironmentInjector,
  EventEmitter,
  Injectable,
  runInInjectionContext,
} from '@angular/core';
import { Messaging, onMessage } from '@angular/fire/messaging';
import { getToken } from 'firebase/messaging';
import { BehaviorSubject, Observable } from 'rxjs';

import { LoggerService } from './logger.service';
import { environment } from '../../../environments/environment';
import { SafeAny } from '../types/safe-any';



@Injectable({ providedIn: 'root' })
export class FirebaseService {
  private serviceWorkerRegistration?: ServiceWorkerRegistration;
  private readonly currentMessage = new BehaviorSubject<SafeAny>(null);
  public newPushNotification = new EventEmitter<SafeAny>();

  constructor(
    private readonly messaging: Messaging,
    private readonly injector: EnvironmentInjector,
    private readonly logger: LoggerService
  ) { }

  private async registerServiceWorker(): Promise<ServiceWorkerRegistration> {
    if (!this.serviceWorkerRegistration) {
      if ('serviceWorker' in navigator) {
        try {
          this.serviceWorkerRegistration = await navigator.serviceWorker.register(
            '/firebase-messaging-sw.js'
          );
        } catch (error) {
          this.logger.error('Service worker registration failed:', error);
          throw error;
        }
      } else {
        throw new Error('Service workers are not supported in this browser');
      }
    }
    return this.serviceWorkerRegistration;
  }

  async requestPermissionAndGetToken(): Promise<string | null> {
    this.logger.log('[FirebaseService] requestPermissionAndGetToken called');
    try {
      if (typeof window !== 'undefined' && window.location?.hostname === 'localhost') {
        this.logger.log('[FirebaseService] Running on localhost, skipping Firebase token');
        return null;
      }
      this.logger.log('[FirebaseService] Requesting notification permission...');
      const permission = await Notification.requestPermission();
      this.logger.log('[FirebaseService] Permission result:', permission);

      if (permission !== 'granted') {
        this.logger.warn('[FirebaseService] Notification permission denied');
        return null;
      }

      this.logger.log('[FirebaseService] Registering service worker...');
      const registration = await this.registerServiceWorker();
      this.logger.log('[FirebaseService] SW registered, getting token...');

      const token = await runInInjectionContext(this.injector, () =>
        getToken(this.messaging, {
          vapidKey: environment.vapidKey,
          serviceWorkerRegistration: registration,
        })
      );

      this.logger.log('[FirebaseService] Token retrieved successfully');
      return token ?? null;
    } catch (err) {
      this.logger.error('[FirebaseService] Error getting permission or token:', err);
      return null;
    }
  }

  listenToMessages(callback?: (payload: SafeAny) => void) {
    runInInjectionContext(this.injector, () => {
      onMessage(this.messaging, (payload) => {
        const notification = payload?.notification ?? payload?.data;

        if (notification) {
          this.currentMessage.next(payload);

          this.newPushNotification.emit({
            notification: {
              title: notification.title ?? 'Nuovo Messaggio',
              body: notification.body ?? '',
            },
            data: payload?.data,
          });

          if (callback) {
            callback(payload);
          }
        }
      });
    });
  }

  getPushNotifications(): Observable<SafeAny> {
    return this.newPushNotification as SafeAny;
  }

  getCurrentMessage(): Observable<SafeAny> {
    return this.currentMessage.asObservable();
  }

  listenToForegroundMessages(cb: (payload: SafeAny) => void) {
    this.listenToMessages(cb);
  }
}
